import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { Plus, Pencil, Trash2, AlertTriangle, FileDown } from "lucide-react";
import { toast } from "sonner";
import { Field, NSelect, TInput } from "@/components/technique/fields";
import PhotoStrip from "@/components/technique/PhotoStrip";
import { SUIVI } from "@/components/technique/suiviSchemas";
import VisToggle from "@/components/technique/VisToggle";

function EntryField({ fd, value, onChange, opts, isNew, kind }) {
  const tid = `${kind}-f-${fd.name}`;
  if (fd.type === "checkbox") return isNew ? <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={tid} />{fd.label}</label> : null;
  if (fd.type === "textarea") return <Field label={fd.label}><Textarea rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} /></Field>;
  if (fd.options) return <Field label={fd.label}><NSelect value={value} onChange={onChange} options={Array.isArray(fd.options) ? fd.options : opts[fd.options]} placeholder={Array.isArray(fd.options) ? "—" : "Aucun"} testid={tid} /></Field>;
  return <Field label={fd.label}><TInput type={fd.type || "text"} value={value} onChange={(v) => onChange(fd.type === "number" ? Number(v) : v)} testid={tid} /></Field>;
}

function EntryDialog({ kind, initial, opts, onClose, onSave }) {
  const sc = SUIVI[kind];
  const [f, setF] = useState(initial);
  const ok = sc.fields.every((fd) => !fd.required || String(f[fd.name] || "").trim());
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`${kind}-dialog`}>
        <DialogHeader><DialogTitle>{initial.id ? "Modifier" : sc.add}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {sc.fields.map((fd) => <div key={fd.name} className={fd.full ? "col-span-2" : ""}>
            <EntryField fd={fd} kind={kind} value={f[fd.name]} onChange={(v) => setF({ ...f, [fd.name]: v })} opts={opts} isNew={!initial.id} /></div>)}
        </div>
        <DialogFooter><Button disabled={!ok} onClick={() => onSave(f)} data-testid={`${kind}-save`} className="bg-brand-600 hover:bg-brand-700 text-white">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EntryRow({ kind, e, readOnly, reload, onEdit, onDelete }) {
  const sc = SUIVI[kind];
  const late = sc.late && sc.late(e);
  const openPdf = async (x) => {
    try { const r = await api.get(`/technique/reports/${x.id}/pdf`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
    catch (err) { toast.error(apiErr(err)); }
  };
  return (
    <div className="px-4 py-3 space-y-2" data-testid={`${kind}-row-${e.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[180px]">
          <p className="font-medium text-sm">{sc.title(e)}</p>
          <p className="text-xs text-slate-500 whitespace-pre-line">{sc.sub(e)}</p>
          {sc.extra(e) && <p className="text-xs text-slate-400 mt-0.5 whitespace-pre-line">{sc.extra(e)}</p>}
        </div>
        {late && <span className="inline-flex items-center gap-1 text-xs text-rose-600 font-semibold"><AlertTriangle className="h-3.5 w-3.5" />Échéance dépassée</span>}
        {sc.badge && <StatusBadge value={sc.badge(e)} />}
        {sc.pdf && <VisToggle kind="report" id={e.id} visible={e.visible_client} testid={`${kind}-vis-${e.id}`} />}
        {sc.pdf && <Button size="sm" variant="outline" className="h-8" onClick={() => openPdf(e)} data-testid={`${kind}-pdf-${e.id}`}><FileDown className="h-4 w-4 mr-1" />PDF</Button>}
        {!readOnly && <>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onEdit} data-testid={`${kind}-edit-${e.id}`}><Pencil className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={onDelete} data-testid={`${kind}-del-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
        </>}
      </div>
      {sc.photos && <div className="flex gap-4 flex-wrap">
        <PhotoStrip kind={kind} entry={e} reload={reload} readOnly={readOnly} tag={sc.correctionPhotos ? "constat" : ""} />
        {sc.correctionPhotos && <PhotoStrip kind={kind} entry={e} reload={reload} readOnly={readOnly} tag="correction" />}
      </div>}
    </div>
  );
}

export default function SuiviSection({ kind, fid, items, opts, readOnly, reload }) {
  const sc = SUIVI[kind];
  const [edit, setEdit] = useState(null);
  const save = async (f) => {
    try {
      if (f.id) await api.put(`/technique/entries/${kind}/${f.id}`, f); else await api.post(`/technique/files/${fid}/entries/${kind}`, f);
      toast.success("Enregistré"); setEdit(null); reload();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (e) => { if (!window.confirm("Supprimer cet élément ?")) return; await api.delete(`/technique/entries/${kind}/${e.id}`); reload(); };
  return (
    <div className="rounded-lg border bg-white overflow-hidden" data-testid={`suivi-${kind}`}>
      <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b">
        <span className="text-sm font-semibold">{sc.label}</span><span className="text-xs text-slate-400">{items.length}</span>
        {!readOnly && <Button size="sm" className="ml-auto bg-brand-600 hover:bg-brand-700 text-white h-8" onClick={() => setEdit(sc.empty())} data-testid={`${kind}-add`}><Plus className="h-4 w-4 mr-1" />{sc.add}</Button>}
      </div>
      {items.length === 0 ? <p className="px-4 py-3 text-sm text-slate-400">Aucun élément.</p> : (
        <div className="divide-y">{items.map((e) => <EntryRow key={e.id} kind={kind} e={e} readOnly={readOnly} reload={reload} onEdit={() => setEdit({ ...e })} onDelete={() => del(e)} />)}</div>
      )}
      {edit && <EntryDialog kind={kind} initial={edit} opts={opts} onClose={() => setEdit(null)} onSave={save} />}
    </div>
  );
}
